// Base URL for the bp-web-components CDN
export const BP_COMPONENTS_URL =
  "https://static.bibleproject.com/bp-web-components";

// Version of bp-web-components to use
export const BP_COMPONENTS_VERSION = "v0.22.0-alpha.9";

// Default title for the app
export const DEFAULT_SEO_TITLE = "Free, Online Courses with BibleProject™";

// Default description for the app
export const DEFAULT_SEO_DESCRIPTION =
  "Self-paced courses in Bible and Christian theology offering training from biblical scholars. Explore our library of classes.";

// Default character limit for notes
export const DEFAULT_NOTE_CHARACTER_LIMIT = 1000;

// Default color for notes
export const DEFAULT_NOTE_COLOR = "cyan";

// Amout of time to wait in between saving session progress
export const SESSION_PROGRESS_SAVE_INTERVAL = 30;

// Minimum version of the mobile app's webview to support
export const MINIMUM_WEBVIEW_VERSION = 2;

// Value used for "unknown" client platform
export const UNKNOWN = "unknown";

// Root URL for the main BibleProject website
export const BIBLEPROJECT_URL = "http://www.bibleproject.com";

// URL for the Help Center
export const HELP_CENTER_URL = "https://help.bibleproject.com/";

// URL for the Help Center page specific to Classroom
export const CLASSROOM_HELP_CENTER_URL =
  "https://help.bibleproject.com/hc/en-us/requests/new?ticket_form_id=12423291731863";

// URL for the BibleProject app in the Apple App Store
export const APP_STORE_URL =
  "https://apps.apple.com/us/app/bibleproject/id1523687027";

// URL for the BibleProject app in the Google Play Store
export const PLAY_STORE_URL =
  "https://play.google.com/store/apps/details?id=com.bibleproject";

// URL for the canonical "Terms of Use" page
export const TERMS_OF_USE_URL = "https://bibleproject.com/terms/";

// URL for the canonical "Privacy Policy" page
export const PRIVACY_POLICY_URL = "https://bibleproject.com/privacy/";

// Cookie names
export const API_AUTH_TOKEN_COOKIE_NAME = "bp-auth-token";
export const API_AUTH_REFRESH_TOKEN_COOKIE_NAME = "bp-auth-refresh";
export const API_AUTH_NONCE_COOKIE_NAME = "bp-auth-nonce";
export const SESSION_COOKIE_NAME = "bp-classroom-session";

/**
 * Bible Translations supported by the Bible Reader.
 */
export enum SupportedBibleTranslation {
  NASB = "NASB",
  AMP = "AMP",
  NIV = "NIV",
  NIRV = "NIRV",
  NLT = "NLT",
}

export const SUPPORTED_BIBLE_TRANSLATIONS = [
  "default",
  ...Object.keys(SupportedBibleTranslation),
] as (keyof typeof SupportedBibleTranslation)[];

export const DEFAULT_BIBLE_TRANSLATION = "NASB";

/**
 * The host context in which the app is running.
 */
export enum HostContext {
  MobileNative = "mobile-native",
  Unknown = "unknown",
}

/**
 * Headers sent by the mobile app to identify itself and
 * provide user context.
 */
export enum BibleProjectHeaders {
  AppVersion = "X-BibleProject-Client-App-Version",
  ClientPlatform = "X-BibleProject-Client-Platform",
  ColorScheme = "X-BibleProject-Client-Color-Scheme",
  WebViewVersion = "X-BibleProject-Client-WebView-Version",
}

/**
 * The media queries used in the app.
 * @see theme.css
 */
export enum MediaQuery {
  sm = "(min-width: 640px)",
  md = "(min-width: 768px)",
  lg = "(min-width: 1024px)",
  xl = "(min-width: 1280px)",
  watchViewDesktop = "(min-width: 1127px)",
}

export const SUPPORTED_BREAKPOINT_KEYS = [
  "default",
  ...Object.keys(MediaQuery),
] as ("default" | keyof typeof MediaQuery)[];

/**
 * Duration values for animations.
 */
export enum Duration {
  Short = 150,
  Normal = 250,
  Long = 400,
}

/**
 * The different types of actions that can be taken.
 */
export enum Action {
  BookmarkClass = "class:bookmark",
  UnbookmarkClass = "class:unbookmark",
  // TODO(CLASS-2816): Cleanup transfer info
  TransferClassData = "class:transfer_data",
  BookmarkModule = "module:bookmark",
  UnbookmarkModule = "module:unbookmark",
  CreateNote = "notebook:create",
  UpdateNote = "notebook:update",
  DeleteNote = "notebook:delete",
  SaveReflectionResponse = "notebook:save_reflection_response",
  FavoriteNote = "notebook:favorite",
  UnfavoriteNote = "notebook:unfavorite",
  SubmitAnswer = "quiz:submit_answer",
  BookmarkSession = "session:bookmark",
  UnbookmarkSession = "session:unbookmark",
  UpdateSessionProgress = "session:update_progress",
  MarkSessionComplete = "session:mark_complete",
  UnmarkSessionComplete = "session:unmark_complete",
  SaveSurveyResponse = "survey:save",
}

/**
 * The different types of routes in the app.
 */
export enum RouteType {
  CLASSROOM = "/",
  HOME = "/home",
  CLASS = "/:class",
  CLASS_COMPLETE = "/:class/completed",
  CLASS_FEEDBACK = "/:class/feedback",
  CLASS_SURVEY = "/:class/completed/survey",
  CLASS_TAB = "/:class/:tab",
  MODULE = "/:class/modules/:module",
  MODULE_TAB = "/:class/modules/:module/:tab",
  NOTEBOOK = "/:class/notebook",
  NOTEBOOK_NOTE = "/:class/notebook/notes/:note",
  NOTEBOOK_TAB = "/:class/notebook/:tab",
  PDF_DOWNLOAD = "/:class/downloads/:type.pdf?d=:param",
  PDF_RENDER = "/_render/:type",
  QUIZ = "/:class/quizzes/:quiz",
  SESSION = "/:class/sessions/:session",
}

/**
 * The different types of quiz question types.
 */
export enum QuizQuestionType {
  FillInBlank = "FILL_IN_BLANK",
  Matching = "MATCHING",
  MultipleChoice = "MULTIPLE_CHOICE",
  Ordering = "ORDERING",
  Response = "RESPONSE",
  SingleChoice = "SINGLE_CHOICE",
  TrueFalse = "TRUE_FALSE",
  Smile = "SMILE",
}
